// Makes a Highcharts legend that does not fit its chart scroll inside its box, with a scrollbar and fade, instead of paging,
// and draws the page arrows the export still uses as the app's chevrons.
// Installed per Highcharts instance from util/highcharts.js#setDefaults; its styles are assets/styles/_legend.scss.

const WHEEL_LINE = 16; // px of one "line" of a mouse wheel that counts lines, not pixels
const MIN_THUMB = 24;
const MAX_FADE = 16; // px, the cards' lists' fade
const FADE_SHARE = 3; // a short legend fades over at most a third of its height

const CLASS_BAR = 'legend-scroll';
const CLASS_FADE = 'legend-scroll-fade';
const CLASS_THUMB = 'legend-scroll-thumb';

// the app's chevron icon: 12 units wide, 6 high, drawn at stroke 1.75
const CHEVRON_HEIGHT_PER_WIDTH = 6 / 12;
const CHEVRON_STROKE_PER_WIDTH = 1.75 / 12;

function drawPageArrowsAsChevrons(legend) {
  const width = legend.options.navigation.arrowSize;
  const top = (width - width * CHEVRON_HEIGHT_PER_WIDTH) / 2;
  const bottom = top + width * CHEVRON_HEIGHT_PER_WIDTH;
  [[legend.up, bottom, top], [legend.down, top, bottom]].forEach(([arrow, ends, tip]) => {
    if (!arrow) {
      return;
    }
    const color = arrow.attr('fill');
    arrow.attr({
      d: `M 0 ${ends} L ${width / 2} ${tip} L ${width} ${ends}`,
      fill: 'none',
      stroke: color === 'none' ? arrow.attr('stroke') : color,
      'stroke-width': width * CHEVRON_STROKE_PER_WIDTH,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    });
  });
}

function scrollTo(legend, top) {
  const next = Math.min(Math.max(top, 0), legend.scrollMax || 0);
  if (next === legend.scrollTop) {
    return false;
  }
  legend.scrollTop = next;
  legend.scrollGroup.attr({ translateY: -next });
  placeScrollBar(legend);
  return true;
}

// (spans: the cards' charts have a rule for every div inside them)
function createScrollBar(legend) {
  const root = document.createElement('span');
  const fade = document.createElement('span');
  const thumb = document.createElement('span');
  root.className = CLASS_BAR;
  fade.className = CLASS_FADE;
  thumb.className = CLASS_THUMB;
  root.appendChild(fade);
  root.appendChild(thumb);
  legend.chart.container.appendChild(root);

  thumb.addEventListener('mousedown', event => {
    event.preventDefault();
    const startY = event.clientY;
    const startTop = legend.scrollTop || 0;
    const move = e => {
      const room = legend.scrollTrack - legend.scrollThumb;
      if (room > 0) {
        scrollTo(legend, startTop + ((e.clientY - startY) * legend.scrollMax) / room);
      }
    };
    const stop = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
  });

  return { root, fade, thumb };
}

function placeScrollBar(legend) {
  const group = legend.group;
  let bar = legend.scrollBar;
  if (!legend.scrollMax || !group || !group.alignAttr || !legend.clipHeight) {
    if (bar) {
      bar.root.style.display = 'none';
    }
    return;
  }
  if (!bar) {
    bar = legend.scrollBar = createScrollBar(legend);
  }
  const track = legend.clipHeight;
  const thumb = Math.min(track, Math.max(MIN_THUMB, (track * track) / (track + legend.scrollMax)));
  const top = (legend.scrollTop / legend.scrollMax) * (track - thumb);
  const fade = Math.min(MAX_FADE, Math.round(track / FADE_SHARE));
  legend.scrollTrack = track;
  legend.scrollThumb = thumb;

  bar.root.style.cssText = `display:block;left:${group.alignAttr.translateX}px;top:${group.alignAttr.translateY + legend.padding}px;width:${legend.legendWidth}px;height:${track}px`;
  bar.thumb.style.cssText = `top:${top}px;height:${thumb}px`;
  bar.fade.style.cssText = `height:${fade}px;display:${legend.scrollTop < legend.scrollMax - 1 ? 'block' : 'none'}`;
}

function listen(legend) {
  const element = legend.group.element;
  if (element.hasScrollingLegend) {
    return;
  }
  element.hasScrollingLegend = true;
  element.addEventListener('wheel', event => {
    const delta = event.deltaMode === 1 ? event.deltaY * WHEEL_LINE : event.deltaY;
    if (legend.scrollMax && scrollTo(legend, (legend.scrollTop || 0) + delta)) {
      event.preventDefault();
    }
  }, { passive: false });
  let touchY = null;
  element.addEventListener('touchstart', event => {
    touchY = event.touches.length === 1 ? event.touches[0].clientY : null;
  }, { passive: true });
  element.addEventListener('touchmove', event => {
    if (touchY === null || !legend.scrollMax) {
      return;
    }
    const y = event.touches[0].clientY;
    const moved = scrollTo(legend, (legend.scrollTop || 0) + (touchY - y));
    touchY = y;
    if (moved) {
      event.preventDefault();
    }
  }, { passive: false });
}

function install(H) {
  const proto = H && H.Legend && H.Legend.prototype;
  if (!proto || proto.hasScrollingLegend) {
    return;
  }
  proto.hasScrollingLegend = true;

  H.wrap(proto, 'handleOverflow', function (proceed, legendHeight) {
    const legend = this;
    const { chart, options, padding } = legend;
    if (options.scrollable === false || (chart.options.chart && chart.options.chart.forExport)) {
      return proceed.call(legend, legendHeight);
    }

    // Highcharts' own layout without its paging; the room below is worked out as its paging does
    const paging = options.navigation.enabled;
    options.navigation.enabled = false;
    proceed.call(legend, legendHeight);
    options.navigation.enabled = paging;

    let space = chart.spacingBox.height + (options.verticalAlign === 'top' ? -options.y : options.y) - padding;
    if (options.layout === 'horizontal' && options.verticalAlign !== 'middle' && !options.floating) {
      space /= 2;
    }
    if (options.maxHeight) {
      space = Math.min(space, options.maxHeight);
    }

    const { renderer } = chart;
    if (legendHeight && space > 0 && legendHeight > space) {
      const clip = Math.max(space - legend.titleHeight - padding, 0);
      legend.clipHeight = clip;
      legend.scrollMax = Math.max(legend.lastItemY + legend.lastLineHeight - (padding + clip), 0);
      if (!legend.clipRect) {
        legend.clipRect = renderer.clipRect(0, padding, 9999, 0);
        legend.contentGroup.clip(legend.clipRect);
      }
      legend.clipRect.attr({ height: clip });
      legend.scrollTop = Math.min(legend.scrollTop || 0, legend.scrollMax);
      legend.scrollGroup.attr({ translateY: -legend.scrollTop });
      listen(legend);
      return space;
    }

    if (legend.clipRect) {
      legend.clipRect = legend.clipRect.destroy();
      legend.contentGroup.clip();
      legend.scrollGroup.attr({ translateY: 0 });
    }
    legend.clipHeight = 0;
    legend.scrollMax = 0;
    legend.scrollTop = 0;
    return legendHeight;
  });

  H.addEvent(H.Legend, 'afterRender', function () {
    placeScrollBar(this);
  });

  H.wrap(proto, 'scroll', function (proceed) {
    const result = proceed.apply(this, Array.prototype.slice.call(arguments, 1));
    drawPageArrowsAsChevrons(this);
    return result;
  });

  H.wrap(proto, 'destroy', function (proceed) {
    if (this.scrollBar) {
      this.scrollBar.root.remove();
      this.scrollBar = null;
    }
    return proceed.apply(this, Array.prototype.slice.call(arguments, 1));
  });
}

export default install;
